import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { LogoMark } from '../Logo';

export const Footer = () => {
  const { t, language } = useLanguage();

  const footerLinks = {
    company: [
      { label: t('aboutUs'), href: '/about' },
      { label: language === 'fr' ? 'Carrières' : 'Careers', href: '/careers' },
      { label: language === 'fr' ? 'Presse' : 'Press', href: '/press' },
      { label: t('contactUs'), href: '/contact' },
    ],
    help: [
      { label: t('helpCenter'), href: '/help' },
      { label: language === 'fr' ? 'Suivi de commande' : 'Track Order', href: '/track' },
      { label: language === 'fr' ? 'Retours' : 'Returns', href: '/returns' },
      { label: language === 'fr' ? 'FAQ' : 'FAQ', href: '/faq' },
      { label: language === 'fr' ? 'Assistance Support' : 'Support Assistance', href: 'https://www.support.respectfulmarkets.com', external: true },
    ],
    sellers: [
      { label: t('sellerCenter'), href: '/seller-center' },
      { label: t('becomeASeller'), href: '/become-seller' },
      { label: language === 'fr' ? 'Tarifs' : 'Pricing', href: '/seller-pricing' },
      { label: language === 'fr' ? 'Ressources' : 'Resources', href: '/seller-resources' },
      { label: language === 'fr' ? 'Programme partenaire' : 'Partner program', href: '/affiliate-program' },
      { label: language === 'fr' ? 'Top affiliés' : 'Top affiliates', href: '/affiliates/leaderboard' },
    ],
    legal: [
      { label: t('privacyPolicy'), href: '/privacy' },
      { label: t('termsOfService'), href: '/terms' },
      { label: language === 'fr' ? 'Cookies' : 'Cookies', href: '/cookies' },
      { label: language === 'fr' ? 'Mentions légales' : 'Legal Notice', href: '/legal' },
    ],
  };

  return (
    <footer className="bg-[#0F4C5C] text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="container-main py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold mb-2">
                {language === 'fr' ? 'Restez informé' : 'Stay Updated'}
              </h3>
              <p className="text-white/70">
                {language === 'fr' 
                  ? 'Inscrivez-vous pour recevoir nos offres exclusives'
                  : 'Subscribe to receive exclusive offers and updates'}
              </p>
            </div>
            <form className="flex gap-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder={language === 'fr' ? 'Votre email' : 'Your email'}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 w-full md:w-64"
              />
              <Button className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-semibold px-6">
                {language === 'fr' ? 'S\'inscrire' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-main py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-flex items-center mb-4">
              <LogoMark size={52} theme="light" />
            </Link>
            <p className="text-white/70 text-sm mb-4">
              {language === 'fr'
                ? 'Votre marketplace de confiance pour des achats responsables.'
                : 'Your trusted marketplace for responsible shopping.'}
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h4 className="font-semibold mb-4">{language === 'fr' ? 'Entreprise' : 'Company'}</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-white/70 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{language === 'fr' ? 'Aide' : 'Help'}</h4>
            <ul className="space-y-2">
              {footerLinks.help.map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/70 hover:text-white text-sm transition-colors"
                      data-testid="support-assistance-link"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="text-white/70 hover:text-white text-sm transition-colors">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{language === 'fr' ? 'Vendeurs' : 'Sellers'}</h4>
            <ul className="space-y-2">
              {footerLinks.sellers.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-white/70 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{language === 'fr' ? 'Légal' : 'Legal'}</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-white/70 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/70">
            <div className="flex flex-wrap items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:contact@respectfulmarkets.com" className="hover:text-white transition-colors">contact@respectfulmarkets.com</a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>+33 1 23 45 67 89</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Paris, France</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10">
        <div className="container-main py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
            <p>© {new Date().getFullYear()} Respectful Markets. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-settings'))}
                className="hover:text-white transition-colors underline-offset-4 hover:underline"
                data-testid="manage-cookies-btn"
              >
                {language === 'fr' ? 'Gérer mes cookies' : 'Manage cookies'}
              </button>
              <span className="text-white/20">·</span>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6 opacity-70" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6 opacity-70" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/1280px-PayPal.svg.png" alt="PayPal" className="h-6 opacity-70" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
